import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Check, X, RefreshCw, Building2, Wallet as WalletIcon } from "lucide-react";

const fmtUsd = (n) => n == null ? "—" : Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AdminWalletOps({ apiBase, token }) {
  const [tab, setTab] = useState("bank");
  return (
    <div className="mt-10 border border-gold/25 bg-surface p-6" data-testid="admin-wallet-ops">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">New · Wallet operations</div>
          <h2 className="font-serif text-2xl text-gold mt-1">Customer Wallets & Bank Transfers</h2>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setTab("bank")}
            data-testid="admin-ops-tab-bank"
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.2em] ${
              tab === "bank" ? "bg-gold text-ink" : "border border-white/10 text-white/60 hover:border-gold"
            }`}
          >
            <Building2 className="h-3.5 w-3.5" /> Bank Transfers
          </button>
          <button
            onClick={() => setTab("wallets")}
            data-testid="admin-ops-tab-wallets"
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.2em] ${
              tab === "wallets" ? "bg-gold text-ink" : "border border-white/10 text-white/60 hover:border-gold"
            }`}
          >
            <WalletIcon className="h-3.5 w-3.5" /> Saved Addresses
          </button>
        </div>
      </div>
      <div className="mt-5">
        {tab === "bank" && <AdminBankTransfers apiBase={apiBase} token={token} />}
        {tab === "wallets" && <AdminSavedAddresses apiBase={apiBase} token={token} />}
      </div>
    </div>
  );
}

function AdminBankTransfers({ apiBase, token }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [busyId, setBusyId] = useState(null);
  const [refOf, setRefOf] = useState({});

  const load = useCallback(async () => {
    try {
      const params = filter === "all" ? "" : `?status=${filter}`;
      const { data } = await axios.get(`${apiBase}/admin/bank-transfers${params}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setRows(data.transfers || []);
    } catch { setRows([]); }
  }, [apiBase, token, filter]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, status) => {
    setBusyId(id + status);
    try {
      await axios.post(`${apiBase}/admin/bank-transfers/${id}/status`,
        { status, ref_number: refOf[id] || null },
        { headers: { Authorization: `Bearer ${token}` } });
      load();
    } finally { setBusyId(null); }
  };

  return (
    <div data-testid="admin-bank-transfers">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1 text-[11px] uppercase tracking-[0.2em]">
          {["pending", "approved", "paid", "rejected", "all"].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`admin-bt-filter-${s}`}
              className={`rounded-full px-3 py-1 ${filter === s ? "bg-gold text-ink" : "border border-white/10 text-white/60 hover:border-gold"}`}>
              {s}
            </button>
          ))}
        </div>
        <button onClick={load} data-testid="admin-bt-refresh" className="ml-auto inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] text-white/50 hover:text-gold">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="mt-4 rounded-lg border border-white/10 bg-black/30 p-6 text-center text-sm text-white/50" data-testid="admin-bt-empty">No bank transfer requests in this view.</div>
      ) : (
        <div className="mt-4 space-y-2" data-testid="admin-bt-list">
          {rows.map((r) => {
            const b = r.beneficiary_snapshot || {};
            return (
              <div key={r.id} className="rounded-lg border border-white/10 bg-black/40 p-4" data-testid={`admin-bt-row-${r.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-serif text-base text-white">${fmtUsd(r.amount_usd)} · {r.code}</div>
                    <div className="text-xs text-white/60">{b.bank_name} · {b.label} · {b.country}</div>
                    <div className="text-xs text-white/50 font-mono">{b.account_number} · {b.account_holder}</div>
                    {b.swift_or_routing && <div className="text-[11px] text-white/40">SWIFT/Route: {b.swift_or_routing}</div>}
                    {r.note && <div className="text-[11px] text-white/40 mt-1">Note: {r.note}</div>}
                    <div className="text-[11px] text-white/40 mt-1">Requested: {new Date(r.created_at).toLocaleString()}</div>
                    {r.ref_number && <div className="text-[11px] text-gold/80 font-mono mt-1">Ref: {r.ref_number}</div>}
                  </div>
                  <div className="text-right">
                    <span className={`inline-block rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] border ${
                      r.status === "pending" ? "bg-amber-500/15 text-amber-200 border-amber-500/40" :
                      r.status === "approved" ? "bg-sky-500/15 text-sky-200 border-sky-500/40" :
                      r.status === "paid" ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/40" :
                      "bg-rose-500/15 text-rose-200 border-rose-500/40"
                    }`}>{r.status}</span>
                  </div>
                </div>
                {(r.status === "pending" || r.status === "approved") && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <input
                      value={refOf[r.id] || ""}
                      onChange={(e) => setRefOf((x) => ({ ...x, [r.id]: e.target.value }))}
                      placeholder="Bank ref # (required for 'paid')"
                      data-testid={`admin-bt-ref-${r.id}`}
                      className="flex-1 min-w-[200px] rounded-lg border border-white/15 bg-black/60 px-3 py-2 text-xs text-white font-mono"
                    />
                    <button
                      onClick={() => act(r.id, "paid")}
                      disabled={busyId === r.id + "paid"}
                      data-testid={`admin-bt-paid-${r.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/50 px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-emerald-200 hover:bg-emerald-500/30 disabled:opacity-50"
                    >
                      {busyId === r.id + "paid" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Mark Paid
                    </button>
                    {r.status === "pending" && (
                      <>
                        <button
                          onClick={() => act(r.id, "approved")}
                          disabled={busyId === r.id + "approved"}
                          data-testid={`admin-bt-approve-${r.id}`}
                          className="inline-flex items-center gap-1.5 rounded-full border border-sky-500/50 px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-sky-200 hover:bg-sky-500/20 disabled:opacity-50"
                        >Approve</button>
                        <button
                          onClick={() => act(r.id, "rejected")}
                          disabled={busyId === r.id + "rejected"}
                          data-testid={`admin-bt-reject-${r.id}`}
                          className="inline-flex items-center gap-1.5 rounded-full border border-rose-500/50 px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-rose-200 hover:bg-rose-500/20 disabled:opacity-50"
                        >
                          <X className="h-3 w-3" /> Reject
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AdminSavedAddresses({ apiBase, token }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${apiBase}/admin/wallet/addresses`,
        { headers: { Authorization: `Bearer ${token}` } });
      setRows(data.addresses || []);
    } catch { setRows([]); }
  }, [apiBase, token]);
  useEffect(() => { load(); }, [load]);

  const filtered = q
    ? rows.filter((r) =>
        [r.code, r.label, r.network, r.address].some((v) => (v || "").toLowerCase().includes(q.toLowerCase()))
      )
    : rows;

  return (
    <div data-testid="admin-saved-addresses">
      <div className="flex items-center gap-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by code, label, network or address…"
          data-testid="admin-wallet-filter"
          className="flex-1 rounded-lg border border-white/15 bg-black/60 px-3 py-2 text-sm text-white" />
        <button onClick={load} data-testid="admin-wallet-refresh" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] text-white/50 hover:text-gold">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>
      {filtered.length === 0 ? (
        <div className="mt-4 rounded-lg border border-white/10 bg-black/30 p-6 text-center text-sm text-white/50" data-testid="admin-wallet-empty">No saved customer addresses yet.</div>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-wallet-table">
            <thead className="text-[10px] uppercase tracking-[0.2em] text-white/40">
              <tr className="border-b border-white/10">
                <th className="py-2 px-2 text-left">Client</th>
                <th className="py-2 px-2 text-left">Label</th>
                <th className="py-2 px-2 text-left">Network</th>
                <th className="py-2 px-2 text-left">Address</th>
                <th className="py-2 px-2 text-left">Saved</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`admin-wallet-row-${r.id}`}>
                  <td className="py-2 px-2 font-mono text-gold/80">{r.code}</td>
                  <td className="py-2 px-2 text-white">{r.label}</td>
                  <td className="py-2 px-2 text-xs uppercase tracking-wider text-white/70">{r.network}</td>
                  <td className="py-2 px-2 font-mono text-xs text-white/60 break-all">{r.address}</td>
                  <td className="py-2 px-2 text-[11px] text-white/40">{new Date(r.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
